// ============================================================
// VALLERJE — Painting Commission: shared UI components
// ============================================================

const h = React.createElement;

const ACCENT = "#e8431c";
const TEXT = "#f0ebe3";
const TEXT_DIM = "#8a8580";
const TEXT_FAINT = "#5a5550";
const SURFACE = "#111110";
const BORDER = "#252523";

// ── Form primitives ─────────────────────────────────────────

function inputStyle(filled) {
  return {
    width: "100%",
    padding: "13px 16px",
    background: SURFACE,
    border: "1px solid " + (filled ? "rgba(232,67,28,0.4)" : BORDER),
    borderRadius: 8,
    color: TEXT,
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s"
  };
}

function focusBind(getFilled) {
  return {
    onFocus: e => { e.target.style.borderColor = ACCENT; },
    onBlur: e => { e.target.style.borderColor = getFilled() ? "rgba(232,67,28,0.4)" : BORDER; }
  };
}

function FormField({ label, hint, required, children }) {
  return h("div", { style: { marginBottom: 24 } },
    h("label", {
      style: {
        display: "block", marginBottom: 8, fontFamily: "'DM Sans',sans-serif",
        fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT_FAINT
      }
    }, label, " ", required && h("span", { style: { color: ACCENT } }, "*")),
    children,
    hint && h("div", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: TEXT_FAINT, marginTop: 6 }
    }, hint)
  );
}

function StepHeading({ eyebrow, title, accent, subtitle }) {
  return h("div", { style: { marginBottom: 32 } },
    eyebrow && h("div", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.18em",
        textTransform: "uppercase", color: ACCENT, marginBottom: 12
      }
    }, eyebrow),
    h("h2", {
      style: {
        fontFamily: "'Bebas Neue',sans-serif", fontSize: "clamp(30px,4.4vw,48px)",
        lineHeight: 1.02, letterSpacing: "0.02em", color: TEXT, margin: 0
      }
    }, title, accent && h("span", { style: { color: ACCENT } }, " ", accent)),
    subtitle && h("p", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 15, lineHeight: 1.7,
        color: TEXT_DIM, margin: "14px 0 0", maxWidth: 620
      }
    }, subtitle)
  );
}

function NavButtons({ onBack, onNext, nextLabel = "Continue", nextDisabled = false, showBack = true, busy = false }) {
  return h("div", {
    className: "vlj-nav-row",
    style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 32, gap: 12 }
  },
    showBack ? h("button", {
      type: "button",
      className: "vlj-btn-secondary",
      onClick: onBack
    }, "← Back") : h("div", null),
    h("button", {
      type: "button",
      className: "vlj-btn vlj-btn--nav",
      onClick: onNext,
      disabled: nextDisabled || busy
    }, busy ? "Sending…" : nextLabel, " ", !nextDisabled && !busy && "→")
  );
}

// ── Progress bar ────────────────────────────────────────────

function ProgressBar({ currentStep, labels, onJump }) {
  const CIRCLE = 28;
  const n = labels.length;
  const completedSegments = Math.max(0, Math.min(n - 1, currentStep - 1));
  const progressPct = n > 1 ? (completedSegments / (n - 1)) * 100 : 0;

  return h("div", { style: { position: "relative", padding: "4px 0" } },
    h("div", {
      "aria-hidden": "true",
      style: {
        position: "absolute", left: (100 / (n * 2)) + "%", right: (100 / (n * 2)) + "%",
        top: 4 + CIRCLE / 2 - 1, height: 2, borderRadius: 2,
        background: "#d6c9b5", opacity: 0.55, pointerEvents: "none"
      }
    }),
    h("div", {
      "aria-hidden": "true",
      style: {
        position: "absolute", left: (100 / (n * 2)) + "%", top: 4 + CIRCLE / 2 - 1,
        height: 2, borderRadius: 2, background: ACCENT,
        width: (((100 - 100 / n) * progressPct) / 100).toFixed(3) + "%",
        transition: "width 0.4s ease", pointerEvents: "none"
      }
    }),
    h("div", { style: { position: "relative", display: "flex", alignItems: "flex-start" } },
      labels.map((label, i) => {
        const num = i + 1;
        const isActive = num === currentStep;
        const isDone = num < currentStep;
        const clickable = isDone && typeof onJump === "function";
        const handleClick = () => { if (clickable) onJump(i); };
        return h("div", {
          key: num,
          role: clickable ? "button" : undefined,
          tabIndex: clickable ? 0 : -1,
          "aria-label": clickable ? "Go back to " + label : undefined,
          onClick: handleClick,
          onKeyDown: clickable ? e => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); }
          } : undefined,
          className: clickable ? "vlj-progress-clickable" : undefined,
          style: {
            flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
            alignItems: "center", gap: 8, cursor: clickable ? "pointer" : "default"
          }
        },
          h("div", {
            style: {
              width: CIRCLE, height: CIRCLE, borderRadius: "50%",
              background: isDone || isActive ? ACCENT : "#1a1612",
              border: isActive || isDone ? "none" : "1.5px solid #d6c9b5",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.3s ease", flexShrink: 0
            }
          },
            isDone
              ? h("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none" },
                  h("polyline", { points: "2,6 5,9 10,3", stroke: "#080807", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }))
              : h("span", {
                  style: { fontSize: 11, fontWeight: 700, color: isActive ? "#080807" : "#a8a097", fontFamily: "'DM Sans',sans-serif" }
                }, num)
          ),
          h("span", {
            className: "vlj-progress-label",
            style: {
              fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
              color: isActive ? ACCENT : isDone ? TEXT : "#a8a097",
              fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap", transition: "color 0.3s"
            }
          }, label)
        );
      })
    )
  );
}

// ── Live quote summary ──────────────────────────────────────

function QuoteRows({ quote, dim }) {
  if (!quote.size) return null;
  const rowStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "baseline",
    gap: 12, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: dim || TEXT_DIM
  };
  return h(React.Fragment, null,
    h("div", { style: { ...rowStyle, marginBottom: 8 } },
      h("span", null, quote.size.name, " · ", quote.size.dimsCm),
      h("span", { style: { color: TEXT, whiteSpace: "nowrap" } }, quote.baseLabel)
    ),
    h("div", { style: { ...rowStyle, marginBottom: 8 } },
      h("span", null,
        quote.extraCharacters > 0
          ? quote.extraCharacters + " extra character" + (quote.extraCharacters > 1 ? "s" : "") + " × " + quote.perCharacterLabel
          : quote.includedCharacters + " character included"
      ),
      h("span", { style: { color: quote.extraCharacters > 0 ? TEXT : TEXT_FAINT, whiteSpace: "nowrap" } },
        quote.extraCharacters > 0 ? quote.charactersTotalLabel : "Included")
    ),
    h("div", { style: rowStyle },
      h("span", null, "Worldwide shipping"),
      h("span", { style: { color: ACCENT, whiteSpace: "nowrap", fontWeight: 600 } }, "Included")
    )
  );
}

function SummaryPanel({ formData }) {
  const quote = buildQuote(formData);
  return h("div", {
    style: {
      position: "sticky", top: 108, background: SURFACE, border: "1px solid " + BORDER,
      borderRadius: 12, padding: "22px 22px 24px"
    }
  },
    h("div", {
      style: {
        fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, letterSpacing: "0.08em",
        color: TEXT_DIM, marginBottom: 18
      }
    }, "Your commission"),

    quote.size
      ? h(React.Fragment, null,
          h(QuoteRows, { quote: quote }),
          h("div", { style: { height: 1, background: BORDER, margin: "16px 0" } }),
          h("div", {
            style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }
          },
            h("span", {
              style: {
                fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.12em",
                textTransform: "uppercase", color: TEXT_FAINT
              }
            }, "Total"),
            h("span", {
              style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 30, color: ACCENT, lineHeight: 1 }
            }, quote.totalLabel)
          )
        )
      : h("p", {
          style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: TEXT_FAINT, lineHeight: 1.7, margin: 0 }
        }, "Pick a size and your price appears here, updating as you build the piece."),

    (formData.theme || formData.country) && h("div", { style: { marginTop: 18, paddingTop: 16, borderTop: "1px solid " + BORDER } },
      formData.theme && h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM, marginBottom: 4 }
      }, "Theme: ", h("span", { style: { color: TEXT } }, formData.theme)),
      formData.country && h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM }
      }, "Ships to: ", h("span", { style: { color: TEXT } }, formData.country))
    )
  );
}

function MobileSummarySheet({ formData }) {
  const quote = buildQuote(formData);
  if (!quote.size) return null;
  return h("div", {
    style: {
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 2147480000,
      background: "rgba(34,29,24,0.97)", backdropFilter: "blur(12px)",
      borderTop: "1px solid #3a3128", padding: "12px 16px",
      paddingBottom: "max(12px, env(safe-area-inset-bottom))",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12
    }
  },
    h("div", { style: { minWidth: 0 } },
      h("div", {
        style: {
          fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: "0.1em",
          textTransform: "uppercase", color: TEXT_FAINT, marginBottom: 2
        }
      }, quote.size.name, " · ", quote.characters, " char", quote.characters > 1 ? "s" : ""),
      h("div", {
        style: { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
      }, quote.size.dimsCm)
    ),
    h("div", {
      style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, color: ACCENT, lineHeight: 1, whiteSpace: "nowrap" }
    }, quote.totalLabel)
  );
}

// ── Trust strip ─────────────────────────────────────────────

function TrustStrip() {
  const points = VLJ_TRUST_POINTS();
  const testimonials = VLJ_TESTIMONIALS();
  const items = points.map(p => ({ text: p, name: null }))
    .concat(testimonials.map(t => ({ text: t.quote, name: t.name })));
  const [idx, setIdx] = React.useState(0);
  const [fade, setFade] = React.useState(true);

  React.useEffect(() => {
    if (items.length <= 1) return;
    const iv = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx(i => (i + 1) % items.length); setFade(true); }, 300);
    }, 4200);
    return () => clearInterval(iv);
  }, [items.length]);

  if (!items.length) return null;
  const item = items[idx % items.length];

  return h("div", {
    style: {
      borderTop: "1px solid #1a1a18", marginTop: 40, padding: "14px 0",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 8, flexWrap: "wrap", textAlign: "center"
    }
  },
    h("span", { style: { color: ACCENT, fontSize: 9, flexShrink: 0 } }, "✦"),
    h("div", {
      style: {
        fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TEXT_DIM,
        opacity: fade ? 1 : 0, transition: "opacity 0.3s",
        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "center"
      }
    },
      h("span", { style: item.name ? { fontStyle: "italic" } : null }, item.name ? "“" + item.text + "”" : item.text),
      item.name && h("span", { style: { color: TEXT_FAINT } }, "— ", item.name)
    )
  );
}

// ── Modal + FAQ ─────────────────────────────────────────────

function Modal({ isOpen, onClose, title, children, maxWidth = 560 }) {
  React.useEffect(() => {
    if (!isOpen) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    // Lock the page behind the modal so a phone doesn't scroll the form
    // underneath while the drawer is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlay = h("div", {
    className: "vlj-portal vlj-modal-backdrop",
    onClick: onClose,
    style: {
      // The form root sets `isolation: isolate`, which would trap this overlay
      // in its own stacking context and let the theme footer and app bars paint
      // over it. Rendering into <body> escapes that entirely.
      position: "fixed", inset: 0, zIndex: 2147483000, background: "rgba(10,8,6,0.82)",
      backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 20, overflowY: "auto"
    }
  },
    h("div", {
      className: "vlj-modal",
      onClick: e => e.stopPropagation(),
      role: "dialog",
      "aria-modal": "true",
      style: {
        width: "100%", maxWidth: maxWidth, maxHeight: "86vh", overflowY: "auto",
        border: "1px solid #3a3128", borderRadius: 14, padding: "26px 26px 30px", margin: "auto",
        background: "#221d18"
      }
    },
      h("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18 }
      },
        h("div", {
          style: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, letterSpacing: "0.04em", color: TEXT }
        }, title),
        h("button", {
          type: "button", onClick: onClose, "aria-label": "Close",
          style: {
            background: "none", border: "1px solid " + BORDER, borderRadius: 8,
            width: 34, height: 34, color: TEXT_DIM, cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0
          }
        }, "×")
      ),
      children
    )
  );

  return ReactDOM.createPortal(overlay, document.body);
}

function FAQButton({ onClick }) {
  return h("button", {
    type: "button",
    className: "vlj-faq-btn",
    onClick: onClick,
    style: {
      position: "fixed", zIndex: 4, display: "flex", alignItems: "center", gap: 8,
      border: "1px solid #3a3128", borderRadius: 999, padding: "11px 18px",
      cursor: "pointer", boxShadow: "0 6px 24px rgba(0,0,0,0.35)"
    }
  },
    h("span", { style: { color: ACCENT, fontSize: 13, fontWeight: 700 } }, "?"),
    h("span", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "#a8a097" }
    }, "Questions")
  );
}

// A floating button unavoidably sits on top of the step's own Back/Continue
// controls at some scroll position, so the FAQ lives in the flow instead,
// directly under the trust strip on every step.
function InlineFAQLink({ onClick }) {
  return h("div", { style: { textAlign: "center", padding: "4px 0 8px" } },
    h("button", {
      type: "button",
      onClick: onClick,
      style: {
        background: "none", border: "none", padding: "10px 4px", cursor: "pointer",
        fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: ACCENT, textDecoration: "underline"
      }
    }, "Questions? Read the FAQ")
  );
}

function FAQDrawer({ isOpen, onClose }) {
  const items = VLJ_FAQ_ITEMS();
  const [open, setOpen] = React.useState(null);
  return h(Modal, { isOpen: isOpen, onClose: onClose, title: "Common questions", maxWidth: 620 },
    h("div", { style: { display: "flex", flexDirection: "column", gap: 10 } },
      items.map((item, i) =>
        h("div", {
          key: i,
          style: { border: "1px solid " + BORDER, borderRadius: 10, overflow: "hidden", background: SURFACE }
        },
          h("button", {
            type: "button",
            onClick: () => setOpen(open === i ? null : i),
            style: {
              width: "100%", textAlign: "left", background: "none", border: "none",
              padding: "15px 18px", cursor: "pointer", display: "flex",
              justifyContent: "space-between", alignItems: "center", gap: 12,
              fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 600, color: TEXT
            }
          },
            h("span", null, item.q),
            h("span", { style: { color: ACCENT, flexShrink: 0, transition: "transform 0.2s", transform: open === i ? "rotate(45deg)" : "none" } }, "+")
          ),
          open === i && h("div", {
            style: {
              padding: "0 18px 16px", fontFamily: "'DM Sans',sans-serif",
              fontSize: 14, lineHeight: 1.75, color: TEXT_DIM
            }
          }, item.a)
        )
      )
    )
  );
}

// ── Size guide ──────────────────────────────────────────────

function SizeGuideModal({ isOpen, onClose }) {
  const sizes = VLJ_SIZES();
  const cell = {
    padding: "12px 10px", fontFamily: "'DM Sans',sans-serif", fontSize: 13,
    color: TEXT_DIM, borderBottom: "1px solid " + BORDER
  };
  const headerCell = {
    ...cell, color: TEXT_FAINT, fontSize: 11, letterSpacing: "0.1em",
    textTransform: "uppercase", fontWeight: 600
  };
  return h(Modal, { isOpen: isOpen, onClose: onClose, title: "Size guide", maxWidth: 560 },
    h("p", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.7, color: TEXT_DIM, marginTop: 0, marginBottom: 14 }
    }, "Every piece is painted on stretched canvas. Measure your wall before choosing — a painting wants roughly two thirds of the furniture it hangs above."),
    h("p", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.7, color: TEXT_DIM, marginTop: 0, marginBottom: 20 }
    }, "Each package is a ", h("span", { style: { color: TEXT } }, "range, not a fixed canvas"),
      ". The composition decides the final proportions — a tall single figure and a wide battle scene want different shapes — so we agree the exact dimensions together before I start. The price is the same anywhere in the range."),
    h("div", { style: { overflowX: "auto" } },
      h("table", { style: { width: "100%", borderCollapse: "collapse", minWidth: 420 } },
        h("thead", null,
          h("tr", null,
            h("th", { style: { ...headerCell, textAlign: "left" } }, "Package"),
            h("th", { style: { ...headerCell, textAlign: "left" } }, "Width × height (cm)"),
            h("th", { style: { ...headerCell, textAlign: "left" } }, "Inches"),
            h("th", { style: { ...headerCell, textAlign: "right" } }, "From")
          )
        ),
        h("tbody", null,
          sizes.map(s =>
            h("tr", { key: s.id },
              h("td", { style: { ...cell, color: TEXT, fontWeight: 600 } }, s.name),
              h("td", { style: cell }, s.dimsCm),
              h("td", { style: cell }, s.dimsIn),
              h("td", { style: { ...cell, textAlign: "right", color: TEXT, whiteSpace: "nowrap" } }, price(s.price))
            )
          )
        )
      )
    ),
    h("p", {
      style: { fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.7, color: TEXT_FAINT, margin: "16px 0 0" }
    }, "Every price includes worldwide shipping, tracked and insured.")
  );
}

Object.assign(window, {
  h, ACCENT, TEXT, TEXT_DIM, TEXT_FAINT, SURFACE, BORDER,
  inputStyle, focusBind, FormField, StepHeading, NavButtons, ProgressBar,
  QuoteRows, SummaryPanel, MobileSummarySheet, TrustStrip,
  Modal, FAQButton, FAQDrawer, InlineFAQLink, SizeGuideModal
});
